import type { PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import {
	type PetitionDetailsPagePrefetchedContext,
	type PetitionDetailsPagePrefetchedUserContext,
	prefetchPageContext,
	prefetchPageUserContext,
} from 'src/app/pages/petitionDetails/pageContext';
import type { Sharer } from 'src/app/pages/petitionDetails/shared/types';
import { headlineReplacements } from 'src/app/pages/petitionDetails/shared/utilities';
import { isLoaded } from 'src/app/shared/utils/async';

import { getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		sharer?: Sharer;
		metaContext: PetitionDetailsMetaContext;
		bestShareVisual: boolean;
	}>;

export const PetitionDetailsFacebook = withPrefetchedData(
	function PetitionDetailsFacebook({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> & PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>) {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { slug, sharerUserId },
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const pageContext = await prefetchPageContext(slug!, context);
			const sharer = sharerUserId
				? await getSharer(sharerUserId, context.utilityContext).catch((): Sharer | undefined => undefined)
				: undefined;
			return {
				...pageContext,
				sharer,
				metaContext: buildMetaContext(context),
				bestShareVisual: context.path.endsWith('/bsv'),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const replacements = headlineReplacements({
				pageData: prefetchedData.pageData.pageData,
				sharer: prefetchedData.sharer,
				i18n,
			});
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				bestShareVisual: prefetchedData.bestShareVisual,
				shareHeadlineOverrides: prefetchedData.sharer
					? {
							title: i18n.translate('fe.components.petition_share.share_text_fb', replacements),
							description: replacements.petition_name as string,
						}
					: undefined,
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
